import type { OrderLine } from '../types'

export interface OrderState {
  currency: string
  lines: OrderLine[]
  total: string
}

interface OrderPanelProps {
  order: OrderState | null
}

/**
 * The running order, exactly as the server has it.
 *
 * It mirrors and never computes. The rule the whole system is built on is that
 * the model extracts intent and code owns the order and the money: totals are
 * computed by the backend, held in the agent's order object and pushed here.
 * Summing the lines locally would, sooner or later, show a customer a number
 * the kitchen never agreed to.
 *
 * `total` is a string (SQL NUMERIC) — displayed as-is, never parsed.
 */
export function OrderPanel({ order }: OrderPanelProps): JSX.Element | null {
  if (!order || order.lines.length === 0) return null

  return (
    <div className="mt-8 w-full max-w-sm px-2">
      <ul>
        {order.lines.map((line, i) => (
          <li
            // The server sends no line id, and the same item can legitimately
            // appear twice, so position is the only stable key available.
            key={`${line.name}-${i}`}
            className="flex justify-between gap-4 border-b border-white/10 py-1.5 text-left text-[14px] text-white/80"
          >
            <span>
              {line.quantity} &times; {line.name}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between pt-3 text-[15px] font-bold text-white">
        <span>Total</span>
        <span>
          {order.total} {order.currency}
        </span>
      </div>
    </div>
  )
}
